import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import type { FormSchema, FormField, ThemeSetting, CustomTheme, RankingItem, ImageChoiceOption } from '../../types/form';
import { evaluateConditionalLogicFull, getDefaultFieldState } from '../../lib/conditionalEngine';
import { submitForm, getPresignedUrl, uploadFile, getSavedProgress, saveProgress, deleteSavedProgress } from '../../lib/api';
import { evaluateCalculation } from '../../lib/calculationEngine';
import { processFieldWithPiping } from '../../lib/pipingEngine';
import { evaluateQuiz, randomizeQuiz, type QuizResult } from '../../lib/quizEngine';
import { QuizResults } from './QuizResults';
import { QuizTimer } from './QuizTimer';
import { useFormAnalytics } from '../../lib/useFormAnalytics';

// Signature Pad Component
interface SignaturePadProps {
  onChange: (dataUrl: string | null) => void;
  disabled?: boolean;
  className?: string;
}

function SignaturePad({ onChange, disabled, className }: SignaturePadProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  const getContext = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return null;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.strokeStyle = '#000';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    }
    return ctx;
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Set canvas size
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;

    // Fill with white background
    const ctx = getContext();
    if (ctx) {
      ctx.fillStyle = '#fff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }
  }, [getContext]);

  const getCoordinates = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    if ('touches' in e) {
      return {
        x: e.touches[0].clientX - rect.left,
        y: e.touches[0].clientY - rect.top,
      };
    }
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
  };

  const startDrawing = (e: React.MouseEvent | React.TouchEvent) => {
    if (disabled) return;
    const ctx = getContext();
    if (!ctx) return;

    const { x, y } = getCoordinates(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing || disabled) return;
    const ctx = getContext();
    if (!ctx) return;

    const { x, y } = getCoordinates(e);
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    if (isDrawing && hasSignature) {
      const canvas = canvasRef.current;
      if (canvas) {
        onChange(canvas.toDataURL('image/png'));
      }
    }
    setIsDrawing(false);
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = getContext();
    if (!canvas || !ctx) return;

    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
    onChange(null);
  };

  return (
    <div className={className}>
      <canvas
        ref={canvasRef}
        className={`w-full h-32 border rounded-lg cursor-crosshair ${disabled ? 'bg-gray-100' : 'bg-white'}`}
        style={{ touchAction: 'none' }}
        onMouseDown={startDrawing}
        onMouseMove={draw}
        onMouseUp={stopDrawing}
        onMouseLeave={stopDrawing}
        onTouchStart={startDrawing}
        onTouchMove={draw}
        onTouchEnd={stopDrawing}
      />
      {hasSignature && !disabled && (
        <button
          type="button"
          onClick={clear}
          className="mt-2 text-sm text-gray-500 hover:text-gray-700"
        >
          Clear signature
        </button>
      )}
    </div>
  );
}

// Ranking Field Component with drag and drop
interface RankingFieldProps {
  value: RankingItem[];
  onChange: (items: RankingItem[]) => void;
  disabled?: boolean;
}

function RankingField({ value, onChange, disabled }: RankingFieldProps) {
  const [items, setItems] = useState<RankingItem[]>(value);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  useEffect(() => {
    setItems(value);
  }, [value]);

  const handleDragStart = (index: number) => {
    if (disabled) return;
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index || disabled) return;

    const newItems = [...items];
    const draggedItem = newItems[draggedIndex];
    newItems.splice(draggedIndex, 1);
    newItems.splice(index, 0, draggedItem);
    setItems(newItems);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    onChange(items);
  };

  const moveItem = (fromIndex: number, direction: 'up' | 'down') => {
    if (disabled) return;
    const toIndex = direction === 'up' ? fromIndex - 1 : fromIndex + 1;
    if (toIndex < 0 || toIndex >= items.length) return;

    const newItems = [...items];
    [newItems[fromIndex], newItems[toIndex]] = [newItems[toIndex], newItems[fromIndex]];
    setItems(newItems);
    onChange(newItems);
  };

  return (
    <div className="space-y-2">
      {items.map((item, index) => (
        <div
          key={item.id}
          draggable={!disabled}
          onDragStart={() => handleDragStart(index)}
          onDragOver={(e) => handleDragOver(e, index)}
          onDragEnd={handleDragEnd}
          className={`flex items-center gap-3 p-3 bg-white border rounded-lg ${
            disabled ? 'cursor-not-allowed opacity-60' : 'cursor-grab active:cursor-grabbing'
          } ${draggedIndex === index ? 'opacity-50 border-[var(--theme-primary)]' : 'border-[var(--theme-border)]'}`}
        >
          <span
            className="flex items-center justify-center w-6 h-6 rounded-full text-xs font-medium"
            style={{ backgroundColor: 'var(--theme-primary)', color: 'white' }}
          >
            {index + 1}
          </span>
          <span className="flex-1" style={{ color: 'var(--theme-text)' }}>
            {item.label}
          </span>
          {!disabled && (
            <div className="flex flex-col gap-0.5">
              <button
                type="button"
                onClick={() => moveItem(index, 'up')}
                disabled={index === 0}
                className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                title="Move up"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                </svg>
              </button>
              <button
                type="button"
                onClick={() => moveItem(index, 'down')}
                disabled={index === items.length - 1}
                className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                title="Move down"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>
          )}
          <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8h16M4 16h16" />
          </svg>
        </div>
      ))}
    </div>
  );
}

// Calculated Field Display Component
interface CalculatedFieldDisplayProps {
  field: FormField;
  values: Record<string, unknown>;
  fields: FormField[];
}

function CalculatedFieldDisplay({ field, values, fields }: CalculatedFieldDisplayProps) {
  const result = evaluateCalculation(field, values, fields);

  return (
    <div
      className="px-4 py-3 bg-gray-50 rounded-lg border border-[var(--theme-border)]"
      style={{ color: 'var(--theme-text)' }}
    >
      <div className="text-2xl font-semibold" style={{ color: 'var(--theme-primary)' }}>
        {result.formatted || '-'}
      </div>
      {result.error && (
        <p className="text-xs text-[var(--theme-error)] mt-1">{result.error}</p>
      )}
    </div>
  );
}

// Address Field Component
interface AddressFieldProps {
  field: FormField;
  value: Record<string, string>;
  onChange: (value: Record<string, string>) => void;
  disabled?: boolean;
  error?: boolean;
}

function AddressField({ field, value, onChange, disabled, error }: AddressFieldProps) {
  const components = field.properties.addressComponents || ['street', 'city', 'state', 'zip', 'country'];

  const handleChange = (key: string, val: string) => {
    onChange({ ...value, [key]: val });
  };

  const inputClass = `w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary)] ${
    error ? 'border-[var(--theme-error)]' : 'border-[var(--theme-border)]'
  } ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-[var(--theme-bg)]'}`;

  const inputStyle = { color: 'var(--theme-text)' };

  return (
    <div className="space-y-3">
      {components.includes('street') && (
        <input
          type="text"
          value={value.street || ''}
          onChange={(e) => handleChange('street', e.target.value)}
          placeholder="Street Address"
          disabled={disabled}
          className={inputClass}
          style={inputStyle}
        />
      )}
      {components.includes('street2') && (
        <input
          type="text"
          value={value.street2 || ''}
          onChange={(e) => handleChange('street2', e.target.value)}
          placeholder="Apartment, suite, etc. (optional)"
          disabled={disabled}
          className={inputClass}
          style={inputStyle}
        />
      )}
      <div className="grid grid-cols-2 gap-3">
        {components.includes('city') && (
          <input
            type="text"
            value={value.city || ''}
            onChange={(e) => handleChange('city', e.target.value)}
            placeholder="City"
            disabled={disabled}
            className={inputClass}
            style={inputStyle}
          />
        )}
        {components.includes('state') && (
          <input
            type="text"
            value={value.state || ''}
            onChange={(e) => handleChange('state', e.target.value)}
            placeholder="State/Province"
            disabled={disabled}
            className={inputClass}
            style={inputStyle}
          />
        )}
      </div>
      <div className="grid grid-cols-2 gap-3">
        {components.includes('zip') && (
          <input
            type="text"
            value={value.zip || ''}
            onChange={(e) => handleChange('zip', e.target.value)}
            placeholder="ZIP/Postal Code"
            disabled={disabled}
            className={inputClass}
            style={inputStyle}
          />
        )}
        {components.includes('country') && (
          <input
            type="text"
            value={value.country || field.properties.defaultCountry || ''}
            onChange={(e) => handleChange('country', e.target.value)}
            placeholder="Country"
            disabled={disabled}
            className={inputClass}
            style={inputStyle}
          />
        )}
      </div>
    </div>
  );
}

// NPS (Net Promoter Score) Field Component
interface NPSFieldProps {
  field: FormField;
  value?: number;
  onChange: (value: number) => void;
  disabled?: boolean;
}

function NPSField({ field, value, onChange, disabled }: NPSFieldProps) {
  const leftLabel = field.properties.npsLeftLabel || 'Not likely at all';
  const rightLabel = field.properties.npsRightLabel || 'Extremely likely';
  const showLabels = field.properties.npsShowLabels !== false;

  const getScoreColor = (score: number) => {
    if (score <= 6) return 'bg-red-500'; // Detractors
    if (score <= 8) return 'bg-yellow-500'; // Passives
    return 'bg-green-500'; // Promoters
  };

  return (
    <div className="space-y-2">
      <div className="flex justify-between gap-1">
        {Array.from({ length: 11 }, (_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => !disabled && onChange(i)}
            disabled={disabled}
            className={`flex-1 h-10 rounded-lg font-medium text-sm transition-all ${
              value === i
                ? `${getScoreColor(i)} text-white scale-110`
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
          >
            {i}
          </button>
        ))}
      </div>
      {showLabels && (
        <div className="flex justify-between text-xs" style={{ color: 'var(--theme-text)', opacity: 0.6 }}>
          <span>{leftLabel}</span>
          <span>{rightLabel}</span>
        </div>
      )}
      {value !== undefined && (
        <div className="text-center text-sm mt-2" style={{ color: 'var(--theme-text)' }}>
          <span className={`inline-block px-3 py-1 rounded-full text-white ${getScoreColor(value)}`}>
            {value <= 6 ? 'Detractor' : value <= 8 ? 'Passive' : 'Promoter'}
          </span>
        </div>
      )}
    </div>
  );
}

// Country/State Field Component
interface CountryStateFieldProps {
  field: FormField;
  value: Record<string, string>;
  onChange: (value: Record<string, string>) => void;
  disabled?: boolean;
}

// Common countries with their states
const COUNTRIES_DATA: Record<string, { name: string; states?: string[] }> = {
  US: { name: 'United States', states: ['Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware', 'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico', 'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming'] },
  CA: { name: 'Canada', states: ['Alberta', 'British Columbia', 'Manitoba', 'New Brunswick', 'Newfoundland and Labrador', 'Nova Scotia', 'Ontario', 'Prince Edward Island', 'Quebec', 'Saskatchewan'] },
  GB: { name: 'United Kingdom', states: ['England', 'Scotland', 'Wales', 'Northern Ireland'] },
  AU: { name: 'Australia', states: ['New South Wales', 'Queensland', 'South Australia', 'Tasmania', 'Victoria', 'Western Australia'] },
  DE: { name: 'Germany' },
  FR: { name: 'France' },
  IT: { name: 'Italy' },
  ES: { name: 'Spain' },
  JP: { name: 'Japan' },
  CN: { name: 'China' },
  IN: { name: 'India' },
  BR: { name: 'Brazil' },
  MX: { name: 'Mexico' },
  IL: { name: 'Israel' },
};

function CountryStateField({ field, value, onChange, disabled }: CountryStateFieldProps) {
  const fieldType = field.properties.countryFieldType || 'country_state';
  const popularCountries = field.properties.popularCountries || ['US', 'GB', 'CA', 'AU'];
  const selectedCountry = value.country || field.properties.defaultCountryCode || '';

  const countryStates = selectedCountry ? COUNTRIES_DATA[selectedCountry]?.states : undefined;

  const selectClass = `w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary)] border-[var(--theme-border)] ${
    disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-[var(--theme-bg)]'
  }`;

  const selectStyle = { color: 'var(--theme-text)' };

  // Sort countries with popular ones first
  const sortedCountries = Object.entries(COUNTRIES_DATA).sort((a, b) => {
    const aPopular = popularCountries.includes(a[0]);
    const bPopular = popularCountries.includes(b[0]);
    if (aPopular && !bPopular) return -1;
    if (!aPopular && bPopular) return 1;
    return a[1].name.localeCompare(b[1].name);
  });

  return (
    <div className="space-y-3">
      <select
        value={selectedCountry}
        onChange={(e) => onChange({ country: e.target.value, state: '', city: '' })}
        disabled={disabled}
        className={selectClass}
        style={selectStyle}
      >
        <option value="">Select country...</option>
        {popularCountries.length > 0 && (
          <>
            {sortedCountries
              .filter(([code]) => popularCountries.includes(code))
              .map(([code, { name }]) => (
                <option key={code} value={code}>{name}</option>
              ))}
            <option disabled>──────────</option>
          </>
        )}
        {sortedCountries
          .filter(([code]) => !popularCountries.includes(code))
          .map(([code, { name }]) => (
            <option key={code} value={code}>{name}</option>
          ))}
      </select>

      {(fieldType === 'country_state' || fieldType === 'country_state_city') && countryStates && (
        <select
          value={value.state || ''}
          onChange={(e) => onChange({ ...value, state: e.target.value, city: '' })}
          disabled={disabled}
          className={selectClass}
          style={selectStyle}
        >
          <option value="">Select state/province...</option>
          {countryStates.map((state) => (
            <option key={state} value={state}>{state}</option>
          ))}
        </select>
      )}

      {fieldType === 'country_state_city' && value.state && (
        <input
          type="text"
          value={value.city || ''}
          onChange={(e) => onChange({ ...value, city: e.target.value })}
          placeholder="City"
          disabled={disabled}
          className={selectClass}
          style={selectStyle}
        />
      )}
    </div>
  );
}

// Image Choice Field Component
interface ImageChoiceFieldProps {
  field: FormField;
  value?: string | string[];
  onChange: (value: string | string[]) => void;
  disabled?: boolean;
}

function ImageChoiceField({ field, value, onChange, disabled }: ImageChoiceFieldProps) {
  const choices = field.properties.imageChoices || [];
  const columns = field.properties.imageChoiceColumns || 3;
  const isMultiple = field.properties.imageChoiceMultiple || false;
  const showLabels = field.properties.imageChoiceShowLabels !== false;

  const selectedValues = Array.isArray(value) ? value : (value ? [value] : []);

  const handleSelect = (choiceValue: string) => {
    if (disabled) return;

    if (isMultiple) {
      const newValues = selectedValues.includes(choiceValue)
        ? selectedValues.filter(v => v !== choiceValue)
        : [...selectedValues, choiceValue];
      onChange(newValues);
    } else {
      onChange(choiceValue);
    }
  };

  return (
    <div className={`grid gap-4 grid-cols-${columns}`} style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}>
      {choices.map((choice: ImageChoiceOption) => {
        const isSelected = selectedValues.includes(choice.value);
        return (
          <button
            key={choice.id}
            type="button"
            onClick={() => handleSelect(choice.value)}
            disabled={disabled}
            className={`group relative rounded-lg border-2 overflow-hidden transition-all ${
              isSelected
                ? 'border-[var(--theme-primary)] ring-2 ring-[var(--theme-primary)] ring-opacity-50'
                : 'border-[var(--theme-border)] hover:border-gray-300'
            } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
          >
            <div className="aspect-square bg-gray-100 overflow-hidden">
              {choice.imageUrl ? (
                <img
                  src={choice.imageUrl}
                  alt={choice.label}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-400">
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
              )}
            </div>
            {showLabels && (
              <div className="p-2 text-center" style={{ color: 'var(--theme-text)' }}>
                <p className="text-sm font-medium">{choice.label}</p>
                {choice.description && (
                  <p className="text-xs opacity-70 mt-0.5">{choice.description}</p>
                )}
              </div>
            )}
            {isSelected && (
              <div className="absolute top-2 right-2 w-6 h-6 bg-[var(--theme-primary)] rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            )}
          </button>
        );
      })}
    </div>
  );
}

// Opinion Scale Field Component
interface OpinionScaleFieldProps {
  field: FormField;
  value?: number;
  onChange: (value: number) => void;
  disabled?: boolean;
}

function OpinionScaleField({ field, value, onChange, disabled }: OpinionScaleFieldProps) {
  const min = field.properties.opinionScaleMin ?? 1;
  const max = field.properties.opinionScaleMax ?? 5;
  const leftLabel = field.properties.opinionScaleLeftLabel || '';
  const rightLabel = field.properties.opinionScaleRightLabel || '';
  const showNumbers = field.properties.opinionScaleShowNumbers !== false;
  const customLabels = field.properties.opinionScaleLabels || [];

  const options = Array.from({ length: max - min + 1 }, (_, i) => min + i);

  const getLabel = (val: number) => {
    const custom = customLabels.find(l => l.value === val);
    return custom?.label || '';
  };

  return (
    <div className="space-y-3">
      <div className="flex items-stretch gap-2">
        {leftLabel && (
          <span className="text-xs self-center pr-2" style={{ color: 'var(--theme-text)', opacity: 0.7 }}>
            {leftLabel}
          </span>
        )}
        <div className="flex-1 flex gap-1">
          {options.map((opt) => {
            const label = getLabel(opt);
            return (
              <button
                key={opt}
                type="button"
                onClick={() => !disabled && onChange(opt)}
                disabled={disabled}
                className={`flex-1 py-3 px-2 rounded-lg border transition-all flex flex-col items-center justify-center ${
                  value === opt
                    ? 'border-[var(--theme-primary)] bg-[var(--theme-primary)] text-white'
                    : 'border-[var(--theme-border)] hover:border-gray-400'
                } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
                style={value !== opt ? { color: 'var(--theme-text)' } : {}}
              >
                {showNumbers && <span className="font-medium">{opt}</span>}
                {label && <span className="text-xs mt-1 opacity-80">{label}</span>}
              </button>
            );
          })}
        </div>
        {rightLabel && (
          <span className="text-xs self-center pl-2" style={{ color: 'var(--theme-text)', opacity: 0.7 }}>
            {rightLabel}
          </span>
        )}
      </div>
    </div>
  );
}

// Appointment Field Component
interface AppointmentFieldProps {
  field: FormField;
  value?: { date: string; time: string };
  onChange: (value: { date: string; time: string }) => void;
  disabled?: boolean;
}

function AppointmentField({ field, value, onChange, disabled }: AppointmentFieldProps) {
  const [selectedDate, setSelectedDate] = useState(value?.date || '');
  const duration = field.properties.appointmentDuration || 30;
  const availability = field.properties.appointmentAvailability || [];
  const minAdvance = field.properties.appointmentMinAdvance || 24;
  const maxAdvance = field.properties.appointmentMaxAdvance || 30;
  const blockedDates = field.properties.blockedDates || [];

  // Calculate min and max dates
  const today = new Date();
  const minDate = new Date(today.getTime() + minAdvance * 60 * 60 * 1000);
  const maxDate = new Date(today.getTime() + maxAdvance * 24 * 60 * 60 * 1000);

  // Get available time slots for selected date
  const getTimeSlots = () => {
    if (!selectedDate) return [];

    const date = new Date(selectedDate);
    const dayOfWeek = date.getDay();
    const dayAvailability = availability.find(a => a.dayOfWeek === dayOfWeek);

    if (!dayAvailability) return [];

    const slots: string[] = [];
    dayAvailability.slots.forEach(slot => {
      const [startHour, startMin] = slot.start.split(':').map(Number);
      const [endHour, endMin] = slot.end.split(':').map(Number);

      let currentTime = startHour * 60 + startMin;
      const endTime = endHour * 60 + endMin;

      while (currentTime + duration <= endTime) {
        const hours = Math.floor(currentTime / 60);
        const mins = currentTime % 60;
        slots.push(`${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`);
        currentTime += duration;
      }
    });

    return slots;
  };

  const timeSlots = getTimeSlots();

  const handleDateChange = (date: string) => {
    setSelectedDate(date);
    onChange({ date, time: '' });
  };

  const handleTimeSelect = (time: string) => {
    onChange({ date: selectedDate, time });
  };

  const isDateBlocked = (date: string) => {
    return blockedDates.includes(date);
  };

  const inputClass = `w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary)] border-[var(--theme-border)] ${
    disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-[var(--theme-bg)]'
  }`;

  const inputStyle = { color: 'var(--theme-text)' };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium mb-1" style={{ color: 'var(--theme-text)', opacity: 0.8 }}>
          Select Date
        </label>
        <input
          type="date"
          value={selectedDate}
          onChange={(e) => !isDateBlocked(e.target.value) && handleDateChange(e.target.value)}
          min={minDate.toISOString().split('T')[0]}
          max={maxDate.toISOString().split('T')[0]}
          disabled={disabled}
          className={inputClass}
          style={inputStyle}
        />
      </div>

      {selectedDate && timeSlots.length > 0 && (
        <div>
          <label className="block text-sm font-medium mb-2" style={{ color: 'var(--theme-text)', opacity: 0.8 }}>
            Select Time ({duration} min slots)
          </label>
          <div className="grid grid-cols-4 gap-2">
            {timeSlots.map((slot) => {
              const isSelected = value?.time === slot;
              return (
                <button
                  key={slot}
                  type="button"
                  onClick={() => handleTimeSelect(slot)}
                  disabled={disabled}
                  className={`py-2 px-3 rounded-lg border text-sm font-medium transition-all ${
                    isSelected
                      ? 'border-[var(--theme-primary)] bg-[var(--theme-primary)] text-white'
                      : 'border-[var(--theme-border)] hover:border-gray-400'
                  } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
                  style={!isSelected ? { color: 'var(--theme-text)' } : {}}
                >
                  {slot}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {selectedDate && timeSlots.length === 0 && (
        <p className="text-sm text-yellow-600 bg-yellow-50 p-3 rounded-lg">
          No available time slots for this date. Please select another date.
        </p>
      )}

      {value?.date && value?.time && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-sm text-green-800">
            <span className="font-medium">Selected:</span> {new Date(value.date).toLocaleDateString()} at {value.time}
          </p>
        </div>
      )}
    </div>
  );
}

// Payment Field Component (Placeholder for Stripe/PayPal integration)
interface PaymentFieldProps {
  field: FormField;
  disabled?: boolean;
}

function PaymentField({ field, disabled }: PaymentFieldProps) {
  const provider = field.properties.paymentProvider || 'stripe';
  const amount = field.properties.paymentAmount;
  const currency = field.properties.paymentCurrency || 'USD';
  const description = field.properties.paymentDescription || '';
  const mode = field.properties.paymentMode || 'test';

  const formatCurrency = (value: number, curr: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: curr,
    }).format(value);
  };

  return (
    <div className={`p-4 border rounded-lg ${disabled ? 'opacity-60' : ''}`} style={{ borderColor: 'var(--theme-border)' }}>
      {/* Payment Amount Display */}
      {amount && (
        <div className="text-center mb-4">
          <p className="text-2xl font-bold" style={{ color: 'var(--theme-primary)' }}>
            {formatCurrency(amount, currency)}
          </p>
          {description && (
            <p className="text-sm mt-1" style={{ color: 'var(--theme-text)', opacity: 0.7 }}>
              {description}
            </p>
          )}
        </div>
      )}

      {/* Payment Provider Info */}
      <div className="flex items-center justify-center gap-2 mb-4">
        {provider === 'stripe' ? (
          <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg">
            <svg className="w-10 h-4" viewBox="0 0 60 25" fill="none">
              <path d="M5 8.5C5 7.12 6.12 6 7.5 6H52.5C53.88 6 55 7.12 55 8.5V16.5C55 17.88 53.88 19 52.5 19H7.5C6.12 19 5 17.88 5 16.5V8.5Z" fill="#635BFF"/>
              <text x="15" y="15" fill="white" fontSize="8" fontWeight="bold">stripe</text>
            </svg>
            <span className="text-xs text-gray-500">Secure payment</span>
          </div>
        ) : (
          <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg">
            <svg className="w-16 h-4" viewBox="0 0 100 25" fill="none">
              <rect width="100" height="25" rx="4" fill="#003087"/>
              <text x="10" y="17" fill="white" fontSize="10" fontWeight="bold">PayPal</text>
            </svg>
            <span className="text-xs text-gray-500">Secure payment</span>
          </div>
        )}
      </div>

      {/* Test Mode Warning */}
      {mode === 'test' && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2 mb-4">
          <p className="text-xs text-yellow-800 text-center">
            Test mode - No real charges will be made
          </p>
        </div>
      )}

      {/* Placeholder for actual payment integration */}
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
        <svg className="w-12 h-12 mx-auto text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
        </svg>
        <p className="text-sm text-gray-500">
          {provider === 'stripe' ? 'Stripe' : 'PayPal'} payment form will load here
        </p>
        <p className="text-xs text-gray-400 mt-1">
          Configure API keys in form settings to enable payments
        </p>
      </div>
    </div>
  );
}

interface FormRendererProps {
  formId: string;
  schema: FormSchema;
  onSuccess?: (result: { submissionId: string; message: string; redirectUrl?: string }) => void;
  onError?: (error: Error) => void;
}

function getThemeStyles(theme: ThemeSetting): React.CSSProperties {
  if (theme === 'light') {
    return {
      '--theme-primary': '#3b82f6',
      '--theme-bg': '#ffffff',
      '--theme-text': '#1f2937',
      '--theme-border': '#d1d5db',
      '--theme-error': '#dc2626',
      '--theme-success': '#16a34a',
    } as React.CSSProperties;
  }

  if (theme === 'dark') {
    return {
      '--theme-primary': '#60a5fa',
      '--theme-bg': '#1f2937',
      '--theme-text': '#f9fafb',
      '--theme-border': '#4b5563',
      '--theme-error': '#f87171',
      '--theme-success': '#4ade80',
    } as React.CSSProperties;
  }

  // Custom theme
  const custom = theme as CustomTheme;
  return {
    '--theme-primary': custom.primary || '#3b82f6',
    '--theme-bg': custom.background || '#ffffff',
    '--theme-text': custom.text || '#1f2937',
    '--theme-border': custom.border || '#d1d5db',
    '--theme-error': custom.error || '#dc2626',
    '--theme-success': custom.success || '#16a34a',
  } as React.CSSProperties;
}

export default function FormRenderer({ formId, schema, onSuccess, onError }: FormRendererProps) {
  const { register, handleSubmit, watch, setValue, reset, formState: { errors, isSubmitting } } = useForm({
    mode: 'onChange', // Real-time validation as user types
    reValidateMode: 'onChange',
  });
  const [currentStep, setCurrentStep] = useState(0);
  const [showValidationSummary, setShowValidationSummary] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [uploadedFiles, setUploadedFiles] = useState<Record<string, string>>({});
  const [signatures, setSignatures] = useState<Record<string, string | null>>({});

  // Save & Resume state
  const [resumeToken, setResumeToken] = useState<string | null>(null);
  const [showResumePrompt, setShowResumePrompt] = useState(false);
  const [savedProgressData, setSavedProgressData] = useState<Record<string, unknown> | null>(null);
  const [savedProgressStep, setSavedProgressStep] = useState<number>(0);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const autoSaveTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Quiz mode state
  const [quizResult, setQuizResult] = useState<QuizResult | null>(null);
  const [showQuizResults, setShowQuizResults] = useState(false);
  const [quizTimerPaused] = useState(false);

  const saveAndResumeSettings = schema.settings.saveAndResume;
  const isSaveEnabled = saveAndResumeSettings?.enabled;
  const quizSettings = schema.settings.quiz;
  const isQuizMode = quizSettings?.enabled;

  // Analytics tracking - count non-page-break fields for totalFields
  const totalFields = useMemo(() => schema.fields.filter(f => f.type !== 'page_break').length, [schema.fields]);
  const totalSteps = useMemo(() => {
    const pageBreaks = schema.fields.filter(f => f.type === 'page_break').length;
    return pageBreaks > 0 ? pageBreaks + 1 : 1;
  }, [schema.fields]);

  const {
    trackFieldFocus,
    trackFieldBlur,
    trackPageChange,
    trackCompletion,
  } = useFormAnalytics({
    formId,
    totalFields,
    totalSteps,
    enabled: true,
  });

  // Process fields for quiz randomization
  const processedFields = useMemo(() => {
    if (!isQuizMode) return schema.fields;
    return randomizeQuiz(
      schema.fields,
      quizSettings?.randomizeQuestions || false,
      quizSettings?.randomizeOptions || false
    );
  }, [schema.fields, isQuizMode, quizSettings?.randomizeQuestions, quizSettings?.randomizeOptions]);

  const watchedValues = watch();

  // Build base required map from field definitions
  const baseRequired = useMemo(() => {
    const required: Record<string, boolean> = {};
    processedFields.forEach((field) => {
      required[field.id] = field.required;
    });
    return required;
  }, [processedFields]);

  // Evaluate conditional logic
  const { fieldStates, pageNavigations } = useMemo(() => {
    return evaluateConditionalLogicFull(schema.conditionalLogic, watchedValues, baseRequired, processedFields);
  }, [schema.conditionalLogic, watchedValues, baseRequired, processedFields]);

  // Detect pages from page_break fields
  const pages = useMemo(() => {
    const pageList: { id: string; title: string; description?: string; startIndex: number; endIndex: number }[] = [];
    let currentPageStart = 0;

    processedFields.forEach((field, index) => {
      if (field.type === 'page_break') {
        // Close previous page
        if (index > currentPageStart || pageList.length === 0) {
          pageList.push({
            id: pageList.length === 0 ? 'page_0' : field.id,
            title: pageList.length === 0 ? 'Page 1' : field.label || `Page ${pageList.length + 1}`,
            description: field.properties.pageDescription,
            startIndex: currentPageStart,
            endIndex: index,
          });
        }
        currentPageStart = index + 1;
      }
    });

    // Add final page if there are remaining fields
    if (currentPageStart < processedFields.length || pageList.length === 0) {
      const lastPageBreak = processedFields.filter(f => f.type === 'page_break').pop();
      pageList.push({
        id: lastPageBreak ? `page_after_${lastPageBreak.id}` : 'page_0',
        title: pageList.length === 0 ? '' : lastPageBreak?.label || `Page ${pageList.length + 1}`,
        description: lastPageBreak?.properties.pageDescription,
        startIndex: currentPageStart,
        endIndex: processedFields.length,
      });
    }

    return pageList;
  }, [processedFields]);

  const hasPages = pages.length > 1;
  const currentPageData = pages[currentStep] || pages[0];

  // Check for saved progress on mount
  useEffect(() => {
    if (!isSaveEnabled) return;

    const checkForSavedProgress = async () => {
      try {
        // Check URL for resume token first
        const urlParams = new URLSearchParams(window.location.search);
        const tokenFromUrl = urlParams.get('resume');

        const result = await getSavedProgress(formId, { token: tokenFromUrl || undefined });

        if (result.found && result.data) {
          setSavedProgressData(result.data);
          setSavedProgressStep(result.currentStep || 0);
          if (result.resumeToken) {
            setResumeToken(result.resumeToken);
          }
          // If token was in URL, auto-resume
          if (tokenFromUrl) {
            handleResumeProgress(result.data, result.currentStep || 0, result.resumeToken || null);
          } else {
            setShowResumePrompt(true);
          }
        }
      } catch (error) {
        console.error('Error checking saved progress:', error);
      }
    };

    checkForSavedProgress();
  }, [formId, isSaveEnabled]);

  // Auto-save functionality
  useEffect(() => {
    if (!isSaveEnabled || !saveAndResumeSettings?.autoSave) return;

    const interval = (saveAndResumeSettings.autoSaveInterval || 30) * 1000;

    // Clear existing timer
    if (autoSaveTimerRef.current) {
      clearInterval(autoSaveTimerRef.current);
    }

    // Set up auto-save timer
    autoSaveTimerRef.current = setInterval(() => {
      const values = watchedValues;
      // Only auto-save if there's data
      const hasData = Object.values(values).some(v => v !== null && v !== undefined && v !== '');
      if (hasData) {
        handleSaveProgress(values, currentStep);
      }
    }, interval);

    return () => {
      if (autoSaveTimerRef.current) {
        clearInterval(autoSaveTimerRef.current);
      }
    };
  }, [isSaveEnabled, saveAndResumeSettings?.autoSave, saveAndResumeSettings?.autoSaveInterval, currentStep]);

  // Handle resume progress
  const handleResumeProgress = useCallback((data: Record<string, unknown>, step: number, token: string | null) => {
    // Reset form with saved data
    reset(data);
    setCurrentStep(step);
    if (token) {
      setResumeToken(token);
    }
    setShowResumePrompt(false);
    setSavedProgressData(null);
  }, [reset]);

  // Handle start fresh
  const handleStartFresh = useCallback(async () => {
    if (resumeToken) {
      try {
        await deleteSavedProgress(formId, resumeToken);
      } catch (error) {
        console.error('Error deleting saved progress:', error);
      }
    }
    setShowResumePrompt(false);
    setSavedProgressData(null);
    setResumeToken(null);
  }, [formId, resumeToken]);

  // Handle manual save
  const handleSaveProgress = useCallback(async (data: Record<string, unknown>, step: number) => {
    if (!isSaveEnabled) return;

    setIsSaving(true);
    setSaveError(null);

    try {
      // Find email field value if present
      const emailField = schema.fields.find(f => f.type === 'email');
      const email = emailField ? (data[emailField.name] as string) : undefined;

      const result = await saveProgress(formId, {
        data,
        currentStep: step,
        resumeToken: resumeToken || undefined,
        email,
      });

      setResumeToken(result.resumeToken);
      setLastSaved(new Date());
    } catch (error) {
      console.error('Error saving progress:', error);
      setSaveError('Failed to save progress');
    } finally {
      setIsSaving(false);
    }
  }, [formId, isSaveEnabled, resumeToken, schema.fields]);

  // Legacy multi-step support
  const isMultiStep = schema.steps && schema.steps.length > 0;
  const steps = schema.steps || [];
  const currentStepData = steps[currentStep];

  // Get fields for current view
  const visibleFields = useMemo(() => {
    // Use page_break-based pages first
    if (hasPages && currentPageData) {
      return processedFields.slice(currentPageData.startIndex, currentPageData.endIndex);
    }
    // Fall back to legacy step-based multi-step
    if (isMultiStep && currentStepData) {
      return processedFields.filter((f) => currentStepData.fieldIds.includes(f.id));
    }
    return processedFields;
  }, [processedFields, hasPages, currentPageData, isMultiStep, currentStepData]);

  async function handleFileUpload(field: FormField, files: FileList) {
    const file = files[0];
    if (!file) return;

    try {
      // Get presigned URL
      const presign = await getPresignedUrl({
        formId,
        fieldId: field.id,
        filename: file.name,
        contentType: file.type,
        size: file.size,
        fieldType: 'file',
      });

      // Upload file
      await uploadFile(presign.uploadId, file, (progress) => {
        setUploadProgress((prev) => ({ ...prev, [field.id]: progress }));
      });

      // Store upload ID
      setUploadedFiles((prev) => ({ ...prev, [field.name]: presign.uploadId }));
      setUploadProgress((prev) => ({ ...prev, [field.id]: 100 }));
    } catch (error) {
      console.error('Upload failed:', error);
      setUploadProgress((prev) => ({ ...prev, [field.id]: -1 }));
    }
  }

  // Handle quiz time up
  const handleQuizTimeUp = useCallback(() => {
    // Auto-submit when time runs out
    handleSubmit(onSubmit)();
  }, []);

  async function onSubmit(data: Record<string, unknown>) {
    try {
      // Merge uploaded file IDs and signatures into data
      const submissionData = { ...data };
      const uploadIds: string[] = [];

      Object.entries(uploadedFiles).forEach(([fieldName, uploadId]) => {
        submissionData[fieldName] = uploadId;
        uploadIds.push(uploadId);
      });

      // Add signatures to submission data
      Object.entries(signatures).forEach(([fieldName, signatureData]) => {
        if (signatureData) {
          submissionData[fieldName] = signatureData;
        }
      });

      // Evaluate quiz if in quiz mode
      let quizEvalResult: QuizResult | null = null;
      if (isQuizMode && quizSettings) {
        quizEvalResult = evaluateQuiz(processedFields, submissionData, quizSettings);
        setQuizResult(quizEvalResult);

        // Add quiz results to submission data
        submissionData._quizScore = quizEvalResult.totalScore;
        submissionData._quizMaxScore = quizEvalResult.maxScore;
        submissionData._quizPercentage = quizEvalResult.percentage;
        submissionData._quizPassed = quizEvalResult.passed;
      }

      const result = await submitForm(formId, submissionData, uploadIds);

      // Track successful form completion
      trackCompletion();

      // Show quiz results instead of normal success
      if (isQuizMode && quizEvalResult) {
        setShowQuizResults(true);
        return;
      }

      if (onSuccess) {
        onSuccess(result);
      }

      if (result.redirectUrl) {
        window.location.href = result.redirectUrl;
      }
    } catch (error) {
      if (onError) {
        onError(error instanceof Error ? error : new Error('Submission failed'));
      }
    }
  }

  // Handle quiz retake
  const handleQuizRetake = useCallback(() => {
    setQuizResult(null);
    setShowQuizResults(false);
    reset(); // Reset form values
    setCurrentStep(0);
  }, [reset]);

  function nextStep() {
    // Check for conditional page navigation (skip_to_page)
    const triggeredNavigation = pageNavigations.find(nav => nav.triggered);
    if (triggeredNavigation && hasPages) {
      const targetIndex = pages.findIndex(p => p.id === triggeredNavigation.skipToPageId);
      if (targetIndex !== -1 && targetIndex > currentStep) {
        setCurrentStep(targetIndex);
        trackPageChange(targetIndex);
        window.scrollTo(0, 0);
        return;
      }
    }

    const maxSteps = hasPages ? pages.length : steps.length;
    if (currentStep < maxSteps - 1) {
      const newStep = currentStep + 1;
      setCurrentStep(newStep);
      trackPageChange(newStep);
      window.scrollTo(0, 0);
    }
  }

  function prevStep() {
    if (currentStep > 0) {
      const newStep = currentStep - 1;
      setCurrentStep(newStep);
      trackPageChange(newStep);
      window.scrollTo(0, 0);
    }
  }

  function goToPage(pageIndex: number) {
    if (pageIndex >= 0 && pageIndex < pages.length) {
      setCurrentStep(pageIndex);
      trackPageChange(pageIndex);
      window.scrollTo(0, 0);
    }
  }

  // Helper to get validation message with custom override
  function getValidationMessage(field: FormField, type: 'required' | 'minLength' | 'maxLength' | 'min' | 'max' | 'pattern' | 'email' | 'url', defaultMsg: string): string {
    return field.properties.validationMessages?.[type] || defaultMsg;
  }

  function renderField(field: FormField) {
    const state = fieldStates[field.id] || getDefaultFieldState(field.required);

    if (!state.visible) return null;

    const isRequired = state.required;
    const isDisabled = !state.enabled;

    const fieldError = errors[field.name];

    // Process value piping for labels, placeholders, and help text
    const piped = processFieldWithPiping(field, watchedValues, processedFields);
    const displayLabel = piped.label;
    const displayPlaceholder = piped.placeholder;
    const displayHelpText = piped.helpText;
    const displayOptions = piped.options || field.options;

    // Build validation rules with custom messages
    const validationRules: Record<string, unknown> = {
      required: isRequired ? getValidationMessage(field, 'required', `${displayLabel} is required`) : false,
      disabled: isDisabled,
    };

    // Add type-specific validation
    if (field.type === 'text' || field.type === 'textarea') {
      if (field.properties.minLength) {
        validationRules.minLength = {
          value: field.properties.minLength,
          message: getValidationMessage(field, 'minLength', `Must be at least ${field.properties.minLength} characters`),
        };
      }
      if (field.properties.maxLength) {
        validationRules.maxLength = {
          value: field.properties.maxLength,
          message: getValidationMessage(field, 'maxLength', `Must be no more than ${field.properties.maxLength} characters`),
        };
      }
      if (field.properties.pattern) {
        validationRules.pattern = {
          value: new RegExp(field.properties.pattern),
          message: getValidationMessage(field, 'pattern', 'Invalid format'),
        };
      }
    }

    if (field.type === 'number') {
      if (field.properties.min !== undefined) {
        validationRules.min = {
          value: field.properties.min,
          message: getValidationMessage(field, 'min', `Must be at least ${field.properties.min}`),
        };
      }
      if (field.properties.max !== undefined) {
        validationRules.max = {
          value: field.properties.max,
          message: getValidationMessage(field, 'max', `Must be no more than ${field.properties.max}`),
        };
      }
    }

    if (field.type === 'email') {
      validationRules.pattern = {
        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
        message: getValidationMessage(field, 'email', 'Please enter a valid email address'),
      };
    }

    const registeredProps = register(field.name, validationRules);
    const commonProps = {
      ...registeredProps,
      placeholder: displayPlaceholder,
      disabled: isDisabled,
      className: `w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary)] ${
        fieldError ? 'border-[var(--theme-error)]' : 'border-[var(--theme-border)]'
      } ${isDisabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-[var(--theme-bg)]'}`,
      style: { color: 'var(--theme-text)' },
      onFocus: () => {
        trackFieldFocus(field.id, field.name);
      },
      onBlur: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        trackFieldBlur(field.id, field.name, !!fieldError);
        registeredProps.onBlur(e); // Call original for validation
      },
    };

    // For layout fields, render differently
    if (field.type === 'section') {
      return (
        <div key={field.id} className="mb-6 mt-6">
          <div className="border-l-4 border-[var(--theme-primary)] pl-4 py-3">
            <h3 className="font-semibold text-lg" style={{ color: 'var(--theme-text)' }}>
              {displayLabel}
            </h3>
            {field.properties.sectionDescription && (
              <p className="text-sm mt-1" style={{ color: 'var(--theme-text)', opacity: 0.7 }}>
                {field.properties.sectionDescription}
              </p>
            )}
          </div>
        </div>
      );
    }

    if (field.type === 'divider') {
      return (
        <div key={field.id} className="my-6">
          <hr
            style={{
              borderStyle: field.properties.dividerStyle || 'solid',
              borderColor: field.properties.dividerColor || 'var(--theme-border)',
            }}
          />
        </div>
      );
    }

    // page_break is handled at a higher level for pagination, skip rendering
    if (field.type === 'page_break') {
      return null;
    }

    return (
      <div key={field.id} className="mb-4">
        <label className="block text-sm font-medium mb-1" style={{ color: 'var(--theme-text)' }}>
          {displayLabel}
          {isRequired && <span className="text-[var(--theme-error)] ml-1">*</span>}
        </label>

        {field.type === 'text' && <input type="text" {...commonProps} />}

        {field.type === 'email' && <input type="email" {...commonProps} />}

        {field.type === 'number' && (
          <input
            type="number"
            {...commonProps}
            min={field.properties.min}
            max={field.properties.max}
            step={field.properties.step}
          />
        )}

        {field.type === 'textarea' && <textarea {...commonProps} rows={4} />}

        {field.type === 'select' && (
          <select {...commonProps} multiple={field.properties.multiple}>
            <option value="">Select an option...</option>
            {displayOptions?.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        )}

        {field.type === 'checkbox' && (
          <div className="space-y-2">
            {displayOptions?.map((opt) => (
              <label key={opt.value} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  value={opt.value}
                  {...register(field.name)}
                  disabled={isDisabled}
                  className="rounded border-[var(--theme-border)]"
                />
                <span style={{ color: 'var(--theme-text)' }}>{opt.label}</span>
              </label>
            ))}
          </div>
        )}

        {field.type === 'radio' && (
          <div className="space-y-2">
            {displayOptions?.map((opt) => (
              <label key={opt.value} className="flex items-center gap-2">
                <input
                  type="radio"
                  value={opt.value}
                  {...register(field.name, { required: isRequired ? `${displayLabel} is required` : false })}
                  disabled={isDisabled}
                  className="border-[var(--theme-border)]"
                />
                <span style={{ color: 'var(--theme-text)' }}>{opt.label}</span>
              </label>
            ))}
          </div>
        )}

        {field.type === 'date' && <input type="date" {...commonProps} />}

        {field.type === 'datetime' && <input type="datetime-local" {...commonProps} />}

        {field.type === 'file' && (
          <div>
            <input
              type="file"
              accept={field.properties.accept}
              onChange={(e) => e.target.files && handleFileUpload(field, e.target.files)}
              disabled={isDisabled}
              className={commonProps.className}
            />
            {uploadProgress[field.id] !== undefined && uploadProgress[field.id] >= 0 && (
              <div className="mt-2">
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[var(--theme-primary)] transition-all"
                    style={{ width: `${uploadProgress[field.id]}%` }}
                  />
                </div>
                <span className="text-xs text-gray-500">
                  {uploadProgress[field.id] === 100 ? 'Uploaded' : `${uploadProgress[field.id]}%`}
                </span>
              </div>
            )}
          </div>
        )}

        {field.type === 'signature' && (
          <SignaturePad
            disabled={isDisabled}
            onChange={(dataUrl) => {
              setSignatures((prev) => ({ ...prev, [field.name]: dataUrl }));
              setValue(field.name, dataUrl || '');
            }}
          />
        )}

        {field.type === 'richtext' && (
          <textarea
            {...commonProps}
            rows={6}
            placeholder={field.placeholder || 'Enter formatted text...'}
          />
        )}

        {field.type === 'phone' && (
          <div className="flex gap-2">
            <select
              {...register(`${field.name}_country`)}
              disabled={isDisabled}
              className={`px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary)] border-[var(--theme-border)] ${isDisabled ? 'bg-gray-100' : 'bg-[var(--theme-bg)]'}`}
              style={{ width: '120px', color: 'var(--theme-text)' }}
            >
              <option value="+1">+1 (US)</option>
              <option value="+44">+44 (UK)</option>
              <option value="+972">+972 (IL)</option>
              <option value="+49">+49 (DE)</option>
              <option value="+33">+33 (FR)</option>
              <option value="+81">+81 (JP)</option>
              <option value="+86">+86 (CN)</option>
              <option value="+91">+91 (IN)</option>
              <option value="+55">+55 (BR)</option>
              <option value="+61">+61 (AU)</option>
            </select>
            <input
              type="tel"
              {...register(field.name, {
                ...validationRules,
                pattern: {
                  value: /^[0-9\s\-()]+$/,
                  message: 'Please enter a valid phone number',
                },
              })}
              placeholder={field.placeholder || 'Phone number'}
              disabled={isDisabled}
              className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary)] ${
                fieldError ? 'border-[var(--theme-error)]' : 'border-[var(--theme-border)]'
              } ${isDisabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-[var(--theme-bg)]'}`}
              style={{ color: 'var(--theme-text)' }}
            />
          </div>
        )}

        {field.type === 'url' && (
          <input
            type="url"
            {...register(field.name, {
              ...validationRules,
              pattern: {
                value: /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/,
                message: getValidationMessage(field, 'url', 'Please enter a valid URL'),
              },
            })}
            placeholder={field.placeholder || 'https://example.com'}
            disabled={isDisabled}
            className={commonProps.className}
          />
        )}

        {field.type === 'rating' && (
          <div className="flex items-center gap-1">
            {Array.from({ length: field.properties.ratingMax || 5 }).map((_, index) => {
              const value = index + 1;
              const icon = field.properties.ratingIcon || 'star';
              return (
                <label key={index} className="cursor-pointer">
                  <input
                    type="radio"
                    value={value}
                    {...register(field.name, validationRules)}
                    disabled={isDisabled}
                    className="sr-only"
                  />
                  <span
                    className={`text-2xl transition-colors ${
                      Number(watchedValues[field.name]) >= value
                        ? 'text-yellow-400'
                        : 'text-gray-300 hover:text-yellow-200'
                    }`}
                  >
                    {icon === 'star' ? '★' : icon === 'heart' ? '♥' : '●'}
                  </span>
                </label>
              );
            })}
            {field.properties.showValue !== false && watchedValues[field.name] && (
              <span className="ml-2 text-sm" style={{ color: 'var(--theme-text)' }}>
                {watchedValues[field.name]} / {field.properties.ratingMax || 5}
              </span>
            )}
          </div>
        )}

        {field.type === 'slider' && (
          <div className="space-y-2">
            <input
              type="range"
              {...register(field.name, validationRules)}
              min={field.properties.sliderMin ?? 0}
              max={field.properties.sliderMax ?? 100}
              step={field.properties.sliderStep ?? 1}
              disabled={isDisabled}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[var(--theme-primary)]"
            />
            <div className="flex justify-between text-xs" style={{ color: 'var(--theme-text)', opacity: 0.6 }}>
              {field.properties.showMinMax !== false && (
                <>
                  <span>{field.properties.sliderMin ?? 0}</span>
                  {field.properties.showValue !== false && (
                    <span className="font-medium" style={{ opacity: 1 }}>
                      {watchedValues[field.name] ?? field.properties.sliderMin ?? 0}
                    </span>
                  )}
                  <span>{field.properties.sliderMax ?? 100}</span>
                </>
              )}
              {field.properties.showMinMax === false && field.properties.showValue !== false && (
                <span className="font-medium w-full text-center" style={{ opacity: 1 }}>
                  {watchedValues[field.name] ?? field.properties.sliderMin ?? 0}
                </span>
              )}
            </div>
          </div>
        )}

        {field.type === 'color' && (
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <input
                type="color"
                {...register(field.name, validationRules)}
                disabled={isDisabled}
                className="w-12 h-10 rounded cursor-pointer border border-[var(--theme-border)]"
              />
              <input
                type="text"
                value={watchedValues[field.name] || '#000000'}
                onChange={(e) => setValue(field.name, e.target.value)}
                disabled={isDisabled}
                placeholder="#000000"
                className={`flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-[var(--theme-primary)] border-[var(--theme-border)] ${isDisabled ? 'bg-gray-100' : 'bg-[var(--theme-bg)]'}`}
                style={{ color: 'var(--theme-text)' }}
              />
            </div>
            {field.properties.colorPresets && field.properties.colorPresets.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {field.properties.colorPresets.map((color) => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setValue(field.name, color)}
                    disabled={isDisabled}
                    className={`w-6 h-6 rounded border-2 transition-transform hover:scale-110 ${
                      watchedValues[field.name] === color ? 'border-[var(--theme-primary)]' : 'border-transparent'
                    }`}
                    style={{ backgroundColor: color }}
                    title={color}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {field.type === 'hidden' && (
          <input
            type="hidden"
            {...register(field.name)}
            value={field.properties.hiddenValue || ''}
          />
        )}

        {field.type === 'matrix' && (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th className="text-left p-2"></th>
                  {field.properties.matrixColumns?.map((col) => (
                    <th
                      key={col.id}
                      className="text-center p-2 text-sm font-medium"
                      style={{ color: 'var(--theme-text)' }}
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {field.properties.matrixRows?.map((row) => (
                  <tr key={row.id} className="border-t border-[var(--theme-border)]">
                    <td className="p-2 text-sm" style={{ color: 'var(--theme-text)' }}>
                      {row.label}
                    </td>
                    {field.properties.matrixColumns?.map((col) => (
                      <td key={col.id} className="text-center p-2">
                        <input
                          type={field.properties.matrixType === 'multiple' ? 'checkbox' : 'radio'}
                          {...register(`${field.name}.${row.id}`)}
                          value={col.value}
                          disabled={isDisabled}
                          className="accent-[var(--theme-primary)]"
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {field.type === 'ranking' && (
          <RankingField
            value={(watchedValues[field.name] as RankingItem[]) || field.properties.rankingItems || []}
            onChange={(items) => setValue(field.name, items)}
            disabled={isDisabled}
          />
        )}

        {field.type === 'calculated' && (
          <CalculatedFieldDisplay
            field={field}
            values={watchedValues}
            fields={processedFields}
          />
        )}

        {field.type === 'address' && (
          <AddressField
            field={field}
            value={watchedValues[field.name] as Record<string, string> || {}}
            onChange={(address) => setValue(field.name, address)}
            disabled={isDisabled}
            error={!!fieldError}
          />
        )}

        {field.type === 'nps' && (
          <NPSField
            field={field}
            value={watchedValues[field.name] as number | undefined}
            onChange={(val) => setValue(field.name, val)}
            disabled={isDisabled}
          />
        )}

        {field.type === 'country' && (
          <CountryStateField
            field={field}
            value={watchedValues[field.name] as Record<string, string> || {}}
            onChange={(val) => setValue(field.name, val)}
            disabled={isDisabled}
          />
        )}

        {field.type === 'image_choice' && (
          <ImageChoiceField
            field={field}
            value={watchedValues[field.name] as string | string[] | undefined}
            onChange={(val) => setValue(field.name, val)}
            disabled={isDisabled}
          />
        )}

        {field.type === 'opinion_scale' && (
          <OpinionScaleField
            field={field}
            value={watchedValues[field.name] as number | undefined}
            onChange={(val) => setValue(field.name, val)}
            disabled={isDisabled}
          />
        )}

        {field.type === 'appointment' && (
          <AppointmentField
            field={field}
            value={watchedValues[field.name] as { date: string; time: string } | undefined}
            onChange={(val) => setValue(field.name, val)}
            disabled={isDisabled}
          />
        )}

        {field.type === 'payment' && (
          <PaymentField
            field={field}
            disabled={isDisabled}
          />
        )}

        {displayHelpText && (
          <p className="text-xs mt-1" style={{ color: 'var(--theme-text)', opacity: 0.6 }}>
            {displayHelpText}
          </p>
        )}

        {fieldError && (
          <p className="text-xs mt-1 text-[var(--theme-error)]">
            {fieldError.message as string}
          </p>
        )}
      </div>
    );
  }

  const themeStyles = getThemeStyles(schema.settings.theme || 'light');
  const isDark = schema.settings.theme === 'dark';

  // Show quiz results
  if (showQuizResults && quizResult && quizSettings) {
    return (
      <div style={themeStyles}>
        <QuizResults
          result={quizResult}
          settings={quizSettings}
          fields={processedFields}
          onRetake={quizSettings.allowRetake ? handleQuizRetake : undefined}
        />
      </div>
    );
  }

  // Show resume prompt dialog
  if (showResumePrompt && savedProgressData) {
    return (
      <div
        className={`max-w-xl mx-auto p-6 rounded-xl ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-lg`}
        style={themeStyles}
      >
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-100 flex items-center justify-center">
            <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold mb-2" style={{ color: 'var(--theme-text)' }}>
            {saveAndResumeSettings?.resumePromptText || 'Continue where you left off?'}
          </h2>
          <p className="text-sm mb-6" style={{ color: 'var(--theme-text)', opacity: 0.7 }}>
            We found your previously saved progress. Would you like to continue from where you left off?
          </p>
          <div className="flex gap-3 justify-center">
            <button
              type="button"
              onClick={handleStartFresh}
              className="px-4 py-2 border border-[var(--theme-border)] rounded-lg hover:bg-gray-50"
              style={{ color: 'var(--theme-text)' }}
            >
              Start Fresh
            </button>
            <button
              type="button"
              onClick={() => handleResumeProgress(savedProgressData, savedProgressStep, resumeToken)}
              className="px-4 py-2 bg-[var(--theme-primary)] text-white rounded-lg hover:opacity-90"
            >
              Resume Progress
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`max-w-xl mx-auto p-6 rounded-xl ${isDark ? 'bg-gray-800' : 'bg-white'} shadow-lg`}
      style={themeStyles}
    >
      {/* Quiz Timer */}
      {isQuizMode && quizSettings?.timeLimitMinutes && (
        <QuizTimer
          timeLimitMinutes={quizSettings.timeLimitMinutes}
          onTimeUp={handleQuizTimeUp}
          paused={quizTimerPaused}
        />
      )}

      {/* Save & Resume Status Bar */}
      {isSaveEnabled && (
        <div className="mb-4 flex items-center justify-between text-xs" style={{ color: 'var(--theme-text)', opacity: 0.7 }}>
          <div className="flex items-center gap-2">
            {isSaving ? (
              <>
                <svg className="w-3 h-3 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                <span>Saving...</span>
              </>
            ) : lastSaved ? (
              <>
                <svg className="w-3 h-3 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>Saved {lastSaved.toLocaleTimeString()}</span>
              </>
            ) : saveAndResumeSettings?.autoSave ? (
              <span>Auto-save enabled</span>
            ) : null}
            {saveError && (
              <span className="text-[var(--theme-error)]">{saveError}</span>
            )}
          </div>
          {saveAndResumeSettings?.showSaveButton !== false && (
            <button
              type="button"
              onClick={() => handleSaveProgress(watchedValues, currentStep)}
              disabled={isSaving}
              className="px-3 py-1 text-xs border border-[var(--theme-border)] rounded hover:bg-gray-50 disabled:opacity-50"
              style={{ color: 'var(--theme-text)' }}
            >
              {isSaving ? 'Saving...' : (saveAndResumeSettings?.saveButtonText || 'Save Progress')}
            </button>
          )}
        </div>
      )}

      {/* Page Progress (for page_break-based multi-page forms) */}
      {hasPages && (
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            {pages.map((page, index) => (
              <div key={page.id} className="flex items-center flex-1">
                <button
                  type="button"
                  onClick={() => goToPage(index)}
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                    index <= currentStep
                      ? 'bg-[var(--theme-primary)] text-white'
                      : 'bg-gray-200 text-gray-500 hover:bg-gray-300'
                  }`}
                >
                  {index + 1}
                </button>
                {index < pages.length - 1 && (
                  <div
                    className={`flex-1 h-1 mx-2 ${
                      index < currentStep ? 'bg-[var(--theme-primary)]' : 'bg-gray-200'
                    }`}
                    style={{ minWidth: '20px' }}
                  />
                )}
              </div>
            ))}
          </div>
          {currentPageData?.title && (
            <div className="text-center">
              <h2 className="text-lg font-semibold" style={{ color: 'var(--theme-text)' }}>
                {currentPageData.title}
              </h2>
              {currentPageData.description && (
                <p className="text-sm" style={{ color: 'var(--theme-text)', opacity: 0.7 }}>
                  {currentPageData.description}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Multi-step Progress (legacy step-based) */}
      {!hasPages && isMultiStep && (
        <div className="mb-8">
          <div className="flex items-center justify-between mb-2">
            {steps.map((step, index) => (
              <div key={step.id} className="flex items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    index <= currentStep
                      ? 'bg-[var(--theme-primary)] text-white'
                      : 'bg-gray-200 text-gray-500'
                  }`}
                >
                  {index + 1}
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={`w-full h-1 mx-2 ${
                      index < currentStep ? 'bg-[var(--theme-primary)]' : 'bg-gray-200'
                    }`}
                    style={{ minWidth: '40px' }}
                  />
                )}
              </div>
            ))}
          </div>
          {currentStepData && (
            <div className="text-center">
              <h2 className="text-lg font-semibold" style={{ color: 'var(--theme-text)' }}>
                {currentStepData.title}
              </h2>
              {currentStepData.description && (
                <p className="text-sm" style={{ color: 'var(--theme-text)', opacity: 0.7 }}>
                  {currentStepData.description}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit, () => setShowValidationSummary(true))}>
        {/* Validation Summary */}
        {showValidationSummary && Object.keys(errors).length > 0 && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="flex-1">
                <h4 className="font-medium text-red-800 mb-2">Please fix the following errors:</h4>
                <ul className="space-y-1">
                  {Object.entries(errors).map(([fieldName, error]) => {
                    const field = processedFields.find(f => f.name === fieldName);
                    return (
                      <li key={fieldName} className="text-sm text-red-700">
                        <button
                          type="button"
                          onClick={() => {
                            const element = document.querySelector(`[name="${fieldName}"]`);
                            element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                            (element as HTMLElement)?.focus();
                          }}
                          className="hover:underline text-left"
                        >
                          <span className="font-medium">{field?.label || fieldName}:</span> {(error as { message?: string })?.message || 'Invalid value'}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
              <button
                type="button"
                onClick={() => setShowValidationSummary(false)}
                className="text-red-400 hover:text-red-600"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {visibleFields.map(renderField)}

        {/* Navigation Buttons */}
        <div className="flex gap-3 mt-6">
          {/* Page-based navigation */}
          {hasPages && currentStep > 0 && (
            <button
              type="button"
              onClick={prevStep}
              className="flex-1 px-4 py-2 border border-[var(--theme-border)] rounded-lg hover:bg-gray-50"
              style={{ color: 'var(--theme-text)' }}
            >
              Previous
            </button>
          )}

          {hasPages && currentStep < pages.length - 1 ? (
            <button
              type="button"
              onClick={nextStep}
              className="flex-1 px-4 py-2 bg-[var(--theme-primary)] text-white rounded-lg hover:opacity-90"
            >
              Next
            </button>
          ) : hasPages ? (
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 bg-[var(--theme-primary)] text-white rounded-lg hover:opacity-90 disabled:opacity-50"
            >
              {isSubmitting ? 'Submitting...' : schema.settings.submitButtonText || 'Submit'}
            </button>
          ) : null}

          {/* Legacy step-based navigation */}
          {!hasPages && isMultiStep && currentStep > 0 && (
            <button
              type="button"
              onClick={prevStep}
              className="flex-1 px-4 py-2 border border-[var(--theme-border)] rounded-lg hover:bg-gray-50"
              style={{ color: 'var(--theme-text)' }}
            >
              Previous
            </button>
          )}

          {!hasPages && isMultiStep && currentStep < steps.length - 1 ? (
            <button
              type="button"
              onClick={nextStep}
              className="flex-1 px-4 py-2 bg-[var(--theme-primary)] text-white rounded-lg hover:opacity-90"
            >
              Next
            </button>
          ) : !hasPages ? (
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-4 py-2 bg-[var(--theme-primary)] text-white rounded-lg hover:opacity-90 disabled:opacity-50"
            >
              {isSubmitting ? 'Submitting...' : schema.settings.submitButtonText || 'Submit'}
            </button>
          ) : null}
        </div>
      </form>
    </div>
  );
}
